import { createSvgEl } from '@learnforge/demo-render';
import type { Projector } from './curve';
import type { RiemannRect } from '../calc/riemann';

export interface RiemannBarsHandle {
  /**
   * Draw one `<polygon>` per subinterval.
   *
   * Projects the four corners of each bar via `projector.worldToScreen`:
   *   (x0, baseline) → (x1, baseline) → (x1, height) → (x0, height)
   *
   * For trapezoid rects (`heightRight` defined), the right-top corner uses
   * `heightRight` so the top edge slants.
   *
   * Bars whose top is physically below the baseline in screen space (SVG y
   * of the top corner > SVG y of the baseline corner) additionally carry the
   * `.calc-bar-negative` class. This screen-space comparison is robust to the
   * caller's world-y orientation (e.g. an inverted y-axis projector).
   *
   * Calling `setBars` again replaces the previous set of polygons.
   */
  setBars(rects: RiemannRect[], baselineWorldY: number, className: string): void;

  /** Remove the `<g>` owned by this handle from the SVG. */
  destroy(): void;
}

/**
 * Create a Riemann bar render layer that owns a `<g>` inside `svg`.
 * Insert it in the correct z-order (after the grid, before the figure).
 */
export function createRiemannBars(svg: SVGSVGElement, projector: Projector): RiemannBarsHandle {
  const group = createSvgEl('g');
  svg.appendChild(group);

  return {
    setBars(rects: RiemannRect[], baselineWorldY: number, className: string): void {
      // Remove all previously rendered polygons.
      while (group.firstChild) {
        group.removeChild(group.firstChild);
      }

      for (const rect of rects) {
        const { x0, x1, height } = rect;
        // Right-top y: use heightRight when present (trapezoid), else height.
        const heightRight = rect.heightRight !== undefined ? rect.heightRight : height;

        // Project the four corners.
        const bl = projector.worldToScreen({ x: x0, y: baselineWorldY }); // bottom-left
        const br = projector.worldToScreen({ x: x1, y: baselineWorldY }); // bottom-right
        const tr = projector.worldToScreen({ x: x1, y: heightRight }); // top-right
        const tl = projector.worldToScreen({ x: x0, y: height }); // top-left

        const polygon = createSvgEl('polygon');

        // SVG points attribute: space-separated "x,y" pairs.
        polygon.setAttribute(
          'points',
          `${bl.x},${bl.y} ${br.x},${br.y} ${tr.x},${tr.y} ${tl.x},${tl.y}`,
        );

        // Apply the caller-supplied class.
        polygon.setAttribute('class', className);

        // A bar is negative when its top is physically below the baseline on screen.
        // We compare screen-space y values (SVG: y increases downward), so "below" means
        // tl.y > bl.y. This is orientation-independent: it works regardless of whether
        // the projector's world-y axis is inverted (as in riemann-lab.ts) or not.
        // Using tl (left top) vs bl (left bottom/baseline) as the representative pair,
        // consistent with using `height` (left value) for trapezoid rects.
        if (tl.y > bl.y) {
          polygon.classList.add('calc-bar-negative');
        }

        group.appendChild(polygon);
      }
    },

    destroy(): void {
      group.remove();
    },
  };
}
